import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import type { Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import type { ReviewToken } from '@sleed/bizcommerce-api';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FormField } from '~/components/system/form-field';
import { DateInput } from '~/components/date-input';
import { ProductCombobox, ProductThumb, type ProductComboboxValue } from '~/components/catalog/bundles/product-combobox';
import { Pill } from '~/components/pill';
import { applyServerValidation, serverMessage } from '~/lib/api/server-error';
import { useReviewMutations, useReviewStatuses, type ReviewKind } from '~/hooks/use-reviews';
import { useContributorCategoriesList } from '~/hooks/use-contributor-categories';
import { reviewSchema, emptyEditorialReview, tokenToForm, formToToken, type ReviewFormValues } from '~/lib/schemas/review';
import { isProductTarget, reviewTargetName, reviewTargetTypeLabel, reviewTargetVariant } from '~/lib/reviews/reviews';
import { ContributorCombobox, type ContributorComboboxValue } from './contributor-combobox';
import { ReviewPhotoGallery } from './review-photos';

interface Props {
	kind: ReviewKind;
	open: boolean;
	/** The review to edit; null on the contributor tab adds a new editorial review. */
	row: ReviewToken | null;
	onOpenChange: (open: boolean) => void;
}

const NONE = '__none__';

function productOf(row: ReviewToken | null): ProductComboboxValue | null {
	return row?.to_id ? { id: row.to_id, cd: row.to_code, nm: row.to_title, img: row.imgUrl } : null;
}

function contributorOf(row: ReviewToken | null): ContributorComboboxValue | null {
	return row?.from_id ? { id: row.from_id, name: `${row.con_fn ?? ''} ${row.con_ln ?? ''}`.trim() } : null;
}

/**
 * Moderate a customer comment (status / rating / title / text / visibility / featured / sort), or add / edit an
 * editorial (contributor) review with its product, contributor, category, reviewer, source and date — the legacy
 * ContributorProductReviews form. Only a product target can be changed here: a review of a contributor, brand, content
 * page or customer keeps its target (the server ignores another one), so it is shown read-only.
 */
export function ReviewEditDialog({ kind, open, row, onOpenChange }: Props) {
	const { t } = useTranslation();
	const { update, create } = useReviewMutations(kind);
	const { data: statuses } = useReviewStatuses();
	const editorial = kind === 'contributor';
	const { data: categories } = useContributorCategoriesList();
	const isEdit = Boolean(row?.id);
	const productTarget = isProductTarget(row?.to_ent);
	const targetName = reviewTargetName(row?.to_title, row?.to_id);

	const form = useForm<ReviewFormValues>({
		resolver: zodResolver(reviewSchema) as Resolver<ReviewFormValues>,
		defaultValues: row ? tokenToForm(row) : emptyEditorialReview(),
		mode: 'onChange'
	});
	const { register, handleSubmit, reset, setError, setValue, control, formState } = form;

	// The pickers show names the form values (ids) do not carry.
	const [product, setProduct] = useState<ProductComboboxValue | null>(null);
	const [contributor, setContributor] = useState<ContributorComboboxValue | null>(null);

	useEffect(() => {
		if (!open) return;
		reset(row ? tokenToForm(row) : emptyEditorialReview());
		setProduct(productOf(row));
		setContributor(contributorOf(row));
	}, [open, row, reset]);

	const onSubmit = handleSubmit(async values => {
		const token = formToToken(values, row, editorial);
		try {
			if (isEdit) await update.mutateAsync(token);
			else await create.mutateAsync(token);
			toast.success(t('common.save'));
			onOpenChange(false);
		} catch (err) {
			if (!applyServerValidation(err, setError)) toast.error(serverMessage(err));
		}
	});

	const tErr = (message?: string) => (message ? t(message, { defaultValue: message }) : undefined);
	const saving = update.isPending || create.isPending;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
				<DialogHeader>
					<DialogTitle>{isEdit ? t('catalog.reviews.editTitle') : t('catalog.reviews.addTitle')}</DialogTitle>
					<DialogDescription>
						{!isEdit
							? t('catalog.reviews.addSubtitle')
							: productTarget
								? (row?.to_title ?? '')
								: `${reviewTargetTypeLabel(t, row?.to_ent, row?.to_ent_nm)} · ${targetName}`}
					</DialogDescription>
				</DialogHeader>
				<form onSubmit={onSubmit} noValidate className="flex flex-col gap-4">
					{editorial && (
						<>
							{productTarget ? (
								<FormField label={t('catalog.reviews.columns.product')} error={tErr(formState.errors.to_id?.message)}>
									<ProductCombobox
										value={product}
										onChange={p => {
											if (p.id == null) return;
											setProduct({ id: p.id, cd: p.cd, nm: p.nm, img: p.img1 });
											setValue('to_id', p.id, { shouldValidate: true, shouldDirty: true });
										}}
									/>
								</FormField>
							) : (
								<FormField label={t('catalog.reviews.columns.target')}>
									<div className="flex items-center gap-2 rounded-md border border-border bg-elevated px-3 py-2">
										<ProductThumb src={row?.imgUrl} alt={row?.to_title} size={32} />
										<div className="min-w-0">
											<Pill variant={reviewTargetVariant(row?.to_ent)}>{reviewTargetTypeLabel(t, row?.to_ent, row?.to_ent_nm)}</Pill>
											<div className="mt-0.5 truncate font-medium text-foreground">{targetName}</div>
										</div>
									</div>
									<span className="text-xs text-foreground-muted">{t('catalog.reviews.targetReadOnly')}</span>
								</FormField>
							)}
							<div className="grid grid-cols-2 gap-3">
								<FormField label={t('catalog.reviews.fields.contributor')}>
									<ContributorCombobox
										value={contributor}
										onChange={c => {
											setContributor(c);
											setValue('from_id', c.id, { shouldDirty: true });
										}}
										onClear={() => {
											setContributor(null);
											setValue('from_id', null, { shouldDirty: true });
										}}
									/>
								</FormField>
								<FormField label={t('catalog.reviews.fields.category')}>
									<Controller
										control={control}
										name="con_cat_id"
										render={({ field }) => (
											<Select
												value={field.value != null ? String(field.value) : NONE}
												onValueChange={v => field.onChange(v === NONE ? null : Number(v))}>
												<SelectTrigger className="w-full">
													<SelectValue />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value={NONE}>{t('common.none')}</SelectItem>
													{(categories ?? []).map(c => (
														<SelectItem key={c.id} value={String(c.id)}>
															{c.nm}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
										)}
									/>
								</FormField>
							</div>
							<div className="grid grid-cols-2 gap-3">
								<FormField label={t('catalog.reviews.fields.reviewer')} error={tErr(formState.errors.reviewer?.message)}>
									<Input {...register('reviewer')} maxLength={300} />
								</FormField>
								<FormField label={t('catalog.reviews.fields.source')} error={tErr(formState.errors.source?.message)}>
									<Input {...register('source')} maxLength={300} />
								</FormField>
							</div>
						</>
					)}
					<div className="grid grid-cols-3 gap-3">
						<FormField label={t('catalog.reviews.columns.status')}>
							<Controller
								control={control}
								name="st"
								render={({ field }) => (
									<Select value={String(field.value)} onValueChange={v => field.onChange(Number(v))}>
										<SelectTrigger className="w-full">
											<SelectValue />
										</SelectTrigger>
										<SelectContent>
											{(statuses ?? []).map(s => (
												<SelectItem key={s.id} value={String(s.id)}>
													{s.nm}
												</SelectItem>
											))}
										</SelectContent>
									</Select>
								)}
							/>
						</FormField>
						<FormField label={t('catalog.reviews.columns.rating')} error={tErr(formState.errors.rating?.message)}>
							<Controller
								control={control}
								name="rating"
								render={({ field }) => (
									<Input
										type="number"
										min={0}
										max={5}
										step={0.5}
										value={field.value ?? ''}
										onChange={e => field.onChange(e.target.value === '' ? null : Number(e.target.value))}
										aria-invalid={Boolean(formState.errors.rating)}
									/>
								)}
							/>
						</FormField>
						{editorial && (
							<FormField label={t('catalog.reviews.columns.date')}>
								<Controller
									control={control}
									name="review_date"
									render={({ field }) => (
										<DateInput
											value={field.value ? new Date(field.value) : null}
											onChange={d => field.onChange(d ? d.toISOString() : null)}
										/>
									)}
								/>
							</FormField>
						)}
					</div>
					<FormField label={t('catalog.reviews.title_field')} error={tErr(formState.errors.tl?.message)}>
						<Input {...register('tl')} maxLength={300} />
					</FormField>
					<FormField label={t('catalog.reviews.comment')}>
						<Textarea {...register('review')} rows={5} />
					</FormField>
					{!editorial && (row?.photos?.length ?? 0) > 0 && (
						<FormField label={t('catalog.reviews.photos.title')}>
							<ReviewPhotoGallery key={row?.id ?? 0} photos={row?.photos} />
						</FormField>
					)}
					<div className="grid grid-cols-3 gap-3">
						<div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
							<Label htmlFor="rv-iv">{t('catalog.reviews.columns.visible')}</Label>
							<Controller
								control={control}
								name="iv"
								render={({ field }) => <Switch id="rv-iv" checked={field.value} onCheckedChange={field.onChange} />}
							/>
						</div>
						<div className="flex items-center justify-between rounded-md border border-border px-3 py-2">
							<Label htmlFor="rv-feat">{t('catalog.reviews.featured')}</Label>
							<Controller
								control={control}
								name="isFeatured"
								render={({ field }) => <Switch id="rv-feat" checked={field.value} onCheckedChange={field.onChange} />}
							/>
						</div>
						<FormField label={t('catalog.reviews.columns.sort')} error={tErr(formState.errors.si?.message)}>
							<Input type="number" {...register('si', { valueAsNumber: true })} />
						</FormField>
					</div>
					<DialogFooter>
						<Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
							{t('common.cancel')}
						</Button>
						<Button type="submit" disabled={saving}>
							{saving ? t('common.saving') : t('common.save')}
						</Button>
					</DialogFooter>
				</form>
			</DialogContent>
		</Dialog>
	);
}
